import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Pencil, Activity, LayoutGrid, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, fmtErr } from "../../lib/api";

function toMin(t) { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; }
function toHHMM(min) { return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`; }
function dayKey(dateStr) { return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" }).toLowerCase(); }

function shapeFor(cap) {
  if (cap <= 2) return "rounded-full w-[78px] h-[78px]";
  if (cap <= 4) return "rounded-2xl w-[88px] h-[88px]";
  return "rounded-2xl w-[128px] h-[76px]";
}

const LIVE_STYLE = {
  free: "bg-emerald-400/10 border-emerald-400/50 shadow-[0_0_22px_-4px_rgba(52,211,153,0.5)]",
  booked: "bg-amber-400/15 border-amber-400/60 shadow-[0_0_22px_-4px_rgba(251,191,36,0.5)]",
  seated: "bg-blue-400/15 border-blue-400/70 shadow-[0_0_22px_-4px_rgba(96,165,250,0.55)]",
  closed: "bg-zinc-500/10 border-zinc-500/30 opacity-60",
};

function CombinePicker({ value, onChange, excludeId, testid, tables }) {
  return (
    <div>
      <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Combines with (large parties)</Label>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {tables.filter((t) => t.id !== excludeId && !t.parent_id).map((t) => {
          const on = (value || []).includes(t.id);
          return (
            <button key={t.id} type="button" data-testid={`${testid}-combine-${t.id}`}
              onClick={() => onChange(on ? value.filter((x) => x !== t.id) : [...(value || []), t.id])}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "border-border/60 text-muted-foreground hover:text-foreground"}`}>
              {t.label} ({t.capacity})
            </button>
          );
        })}
        {tables.filter((t) => t.id !== excludeId && !t.parent_id).length === 0 && (
          <p className="text-xs text-muted-foreground">Add more tables first.</p>
        )}
      </div>
    </div>
  );
}

export default function FloorPlan({ restaurant, canEdit }) {
  const containerRef = useRef(null);
  const [tables, setTables] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [bookings, setBookings] = useState([]);
  const [mode, setMode] = useState("live");
  const [time, setTime] = useState("");
  const [liveSel, setLiveSel] = useState(null);
  const [selId, setSelId] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", capacity: "4", combines_with: [] });
  const [editTable, setEditTable] = useState(null);

  const load = () => {
    api.get(`/restaurants/${restaurant.id}/tables`).then((r) => setTables(r.data)).catch(() => {});
    api.get(`/restaurants/${restaurant.id}/bookings`, { params: { date } }).then((r) => setBookings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [restaurant.id, date]);

  useEffect(() => {
    if (mode !== "live") return;
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [mode, restaurant.id, date]);

  const duration = restaurant.booking_policy?.slot_duration ?? 90;
  const day = restaurant.operating_hours?.[dayKey(date)];
  const closedDates = restaurant.booking_policy?.closed_dates || [];
  const closedDay = !day || day.closed || closedDates.includes(date);

  const slots = useMemo(() => {
    if (closedDay) return [];
    const wd = dayKey(date);
    const svcs = (restaurant.services || []).filter((s) => s.enabled !== false && s.open && s.close &&
      (!s.days?.length || s.days.some((d) => String(d).slice(0, 3).toLowerCase() === wd)));
    const ranges = svcs.length ? svcs.map((s) => [s.open, s.close]) : [[day.open, day.close]];
    const out = [];
    ranges.forEach(([o, c]) => { for (let m = toMin(o); m < toMin(c); m += 30) out.push(toHHMM(m)); });
    return [...new Set(out)];
  }, [date, restaurant, closedDay]);

  useEffect(() => {
    if (!slots.length) { setTime(""); return; }
    if (time && slots.includes(time)) return;
    if (date === new Date().toISOString().slice(0, 10)) {
      const nm = new Date().getHours() * 60 + new Date().getMinutes();
      setTime(slots.find((s) => nm < toMin(s) + 30) || slots[slots.length - 1]);
    } else {
      setTime(slots[0]);
    }
  }, [slots, date]);

  const activeBookings = bookings.filter((b) => !["cancelled", "no_show"].includes(b.status));
  const expandIds = (ids) => {
    const out = new Set(ids.filter(Boolean));
    ids.forEach((id) => {
      const t = tables.find((x) => x.id === id);
      if (t?.parent_id) out.add(t.parent_id);
      tables.forEach((x) => { if (x.parent_id === id) out.add(x.id); });
    });
    return out;
  };
  const bookedIds = new Set(activeBookings.filter((b) => ["confirmed", "seated"].includes(b.status))
    .flatMap((b) => [...expandIds(b.table_ids || [b.table_id])]));

  const bookingAt = (t) => {
    if (!time) return null;
    const T = toMin(time);
    return activeBookings.find((b) =>
      expandIds(b.table_ids || [b.table_id]).has(t.id) &&
      toMin(b.time) <= T && T < toMin(b.time) + (b.duration || duration)) || null;
  };

  const statusFor = (t) => {
    if (closedDay) return "closed";
    const b = bookingAt(t);
    if (!b) return "free";
    return b.status === "seated" ? "seated" : "booked";
  };

  const addTable = async () => {
    try {
      await api.post(`/restaurants/${restaurant.id}/tables`, {
        label: form.label, capacity: Number(form.capacity), x: 40, y: 40, combines_with: form.combines_with,
      });
      toast.success("Table added");
      setOpen(false);
      setForm({ label: "", capacity: "4", combines_with: [] });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/restaurants/${restaurant.id}/tables/${editTable.id}`, {
        label: editTable.label, capacity: Number(editTable.capacity), combines_with: editTable.combines_with || [],
      });
      toast.success("Table updated");
      setEditTable(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const moveTable = async (t, info) => {
    const rect = containerRef.current.getBoundingClientRect();
    const nx = Math.min(94, Math.max(0, t.x + (info.offset.x / rect.width) * 100));
    const ny = Math.min(88, Math.max(0, t.y + (info.offset.y / rect.height) * 100));
    try {
      await api.put(`/restaurants/${restaurant.id}/tables/${t.id}`, { x: Math.round(nx / 2) * 2, y: Math.round(ny / 2) * 2 });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const splitTable = async (parent, parts) => {
    try {
      const letters = ["a", "b", "c", "d"];
      const base = Math.floor(Number(parent.capacity) / parts);
      for (let i = 0; i < parts; i++) {
        await api.post(`/restaurants/${restaurant.id}/tables`, {
          label: `${parent.label}${letters[i]}`,
          capacity: base + (i === 0 ? Number(parent.capacity) % parts : 0),
          x: 10 + i * 6, y: 10,
          parent_id: parent.id,
        });
      }
      toast.success(`Split ${parent.label} into ${parts} sub-tables`);
      setEditTable(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const removeTable = async (tid) => {
    try {
      await api.delete(`/restaurants/${restaurant.id}/tables/${tid}`);
      toast.success("Table removed");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div data-testid="floorplan-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <Input data-testid="floorplan-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="bg-card border-border/60 max-w-[200px]" />
          {mode === "live" && !closedDay && slots.length > 0 && (
            <Select value={time} onValueChange={setTime}>
              <SelectTrigger data-testid="floorplan-time" className="w-[120px] bg-card border-border/60">
                <SelectValue placeholder="Time" />
              </SelectTrigger>
              <SelectContent>
                {slots.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`floorplan-time-${s.replace(":", "")}`}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {mode === "live" ? (
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground" data-testid="floorplan-live-legend">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400" /> Free</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> Booked</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-400" /> Seated</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-zinc-500" /> Closed</span>
            </div>
          ) : (
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400" /> Free</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-primary" /> Booked</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button data-testid="floorplan-live-btn" size="sm" variant={mode === "live" ? "default" : "outline"} className="rounded-full"
            onClick={() => setMode("live")}>
            <Activity className="w-4 h-4 mr-2" /> Live view
          </Button>
          {canEdit && (
            <Button data-testid="floorplan-edit-btn" size="sm" variant={mode === "edit" ? "default" : "outline"} className="rounded-full"
              onClick={() => setMode("edit")}>
              <LayoutGrid className="w-4 h-4 mr-2" /> Edit layout
            </Button>
          )}
          {canEdit && mode === "edit" && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-table-btn" size="sm" className="rounded-full"><Plus className="w-4 h-4 mr-2" /> Add table</Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border/60">
                <DialogHeader><DialogTitle className="font-outfit">New table</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-2">
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Label</Label>
                    <Input data-testid="table-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                      className="mt-1.5 bg-background border-border/60" placeholder="e.g. T9" />
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Capacity</Label>
                    <Input data-testid="table-capacity-input" type="number" min="1" value={form.capacity}
                      onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="mt-1.5 bg-background border-border/60" />
                  </div>
                  <CombinePicker testid="add-table" tables={tables} value={form.combines_with}
                    onChange={(v) => setForm({ ...form, combines_with: v })} />
                  <Button data-testid="table-create-btn" onClick={addTable} disabled={!form.label} className="w-full rounded-full">Add table</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {mode === "live" && closedDay && (
        <p data-testid="floorplan-closed-msg" className="text-xs text-muted-foreground mb-3">
          This day is closed — all tables shown greyed out.
        </p>
      )}
      {mode === "live" && !closedDay && (
        <p className="text-xs text-muted-foreground mb-3">Live status at {time} · refreshes every 15 seconds. Tap a table for booking details.</p>
      )}

      <div ref={containerRef} data-testid="floorplan-canvas"
        className="relative w-full h-[480px] rounded-3xl border border-border/40 bg-card/60 overflow-hidden shadow-inner"
        style={{ backgroundImage: "radial-gradient(circle, hsl(var(--muted-foreground) / 0.16) 1px, transparent 1px)", backgroundSize: "32px 32px" }}>
        {canEdit && mode === "edit" && tables.length > 0 && (
          <p className="absolute bottom-3 left-4 text-xs text-muted-foreground/70 pointer-events-none z-10">
            Drag tables to rearrange — they snap to the grid. Tap a table to select it.
          </p>
        )}
        {tables.filter((t) => !t.parent_id).map((t, i) => {
          const b = mode === "live" ? bookingAt(t) : null;
          const st = mode === "live" ? statusFor(t) : (bookedIds.has(t.id) ? "booked" : "free");
          const cls = mode === "live"
            ? LIVE_STYLE[st]
            : bookedIds.has(t.id) ? "bg-primary/15 border-primary/60" : "bg-emerald-400/10 border-emerald-400/40";
          const selected = mode === "edit" && selId === t.id;
          return (
            <motion.div key={`${t.id}-${t.x}-${t.y}-${st}-${time}`} data-testid={`table-${t.id}`}
              drag={canEdit && mode === "edit"} dragMomentum={false} dragConstraints={containerRef}
              onDragEnd={(e, info) => canEdit && mode === "edit" && moveTable(t, info)}
              onTap={() => { if (mode === "live") setLiveSel({ table: t, booking: b }); else if (canEdit) setSelId(selected ? null : t.id); }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25, delay: Math.min(i * 0.05, 0.4) }}
              style={{ left: `${t.x}%`, top: `${t.y}%` }}
              className={`group absolute select-none border flex flex-col items-center justify-center text-center transition-shadow duration-200 ${
                shapeFor(Number(t.capacity) || 4)
              } ${canEdit && mode === "edit" ? "cursor-grab active:cursor-grabbing" : mode === "live" ? "cursor-pointer" : ""
              } ${cls} ${selected ? "ring-2 ring-primary ring-offset-2 ring-offset-card" : ""} hover:shadow-xl`}>
              <p className="text-sm font-outfit font-semibold leading-none">{t.label}</p>
              <span className="mt-1 inline-block rounded-full bg-background/80 border border-border/50 px-1.5 py-px text-[9px] text-muted-foreground leading-tight">
                {t.capacity}
              </span>
              {mode === "live" && (
                <p data-testid={`table-status-${t.id}`} className="mt-0.5 text-[10px] font-medium truncate max-w-[110px] leading-tight">
                  {st === "free" ? "Free" : st === "closed" ? "Closed" : b?.name || st}
                </p>
              )}
              {canEdit && mode === "edit" && (
                <button data-testid={`delete-table-${t.id}`} onClick={(e) => { e.stopPropagation(); removeTable(t.id); }}
                  className={`absolute -top-2.5 -right-2.5 w-6 h-6 rounded-full bg-destructive text-destructive-foreground shadow-md items-center justify-center transition-opacity flex ${
                    selected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
              {canEdit && mode === "edit" && (
                <button data-testid={`edit-table-${t.id}`}
                  onClick={(e) => { e.stopPropagation(); setEditTable({ id: t.id, label: t.label, capacity: String(t.capacity), combines_with: t.combines_with || [] }); }}
                  className={`absolute -top-2.5 -left-2.5 w-6 h-6 rounded-full bg-card border border-border/60 shadow-md text-muted-foreground hover:text-foreground items-center justify-center transition-opacity flex ${
                    selected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
                  <Pencil className="w-3 h-3" />
                </button>
              )}
              {selected && (
                <span className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 w-6 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md pointer-events-none">
                  <GripVertical className="w-3 h-3" />
                </span>
              )}
            </motion.div>
          );
        })}
        {tables.length === 0 && (
          <p className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm" data-testid="no-tables-msg">
            No tables yet — add your first table to start taking bookings.
          </p>
        )}
      </div>

      <Dialog open={!!liveSel} onOpenChange={() => setLiveSel(null)}>
        <DialogContent className="bg-card border-border/60">
          {liveSel && (
            <>
              <DialogHeader><DialogTitle className="font-outfit">{liveSel.table.label} · {time}</DialogTitle></DialogHeader>
              {liveSel.booking ? (
                <div className="text-sm text-muted-foreground space-y-1.5" data-testid="floorplan-booking-detail">
                  <p className="text-foreground font-medium">{liveSel.booking.name} · party of {liveSel.booking.party_size}</p>
                  <p className="capitalize">{liveSel.booking.date} at {liveSel.booking.time} · {liveSel.booking.status.replace("_", " ")}</p>
                  <p>{liveSel.booking.email}{liveSel.booking.phone ? ` · ${liveSel.booking.phone}` : ""}</p>
                  {liveSel.booking.occasion && <p className="text-primary">Occasion: {liveSel.booking.occasion}</p>}
                  {liveSel.booking.dietary && <p className="text-amber-500">Dietary: {liveSel.booking.dietary}</p>}
                  {liveSel.booking.notes && <p>Notes: {liveSel.booking.notes}</p>}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="floorplan-table-free">
                  No booking at {time} — this table is free.
                </p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTable} onOpenChange={() => setEditTable(null)}>
        <DialogContent className="bg-card border-border/60">
          {editTable && (
            <>
              <DialogHeader><DialogTitle className="font-outfit">Edit {editTable.label}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2" data-testid="edit-table-dialog">
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Label</Label>
                  <Input data-testid="edit-table-label" value={editTable.label}
                    onChange={(e) => setEditTable({ ...editTable, label: e.target.value })} className="mt-1.5 bg-background border-border/60" />
                </div>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Capacity</Label>
                  <Input data-testid="edit-table-capacity" type="number" min="1" value={editTable.capacity}
                    onChange={(e) => setEditTable({ ...editTable, capacity: e.target.value })} className="mt-1.5 bg-background border-border/60" />
                </div>
                <CombinePicker testid="edit-table" tables={tables} excludeId={editTable.id} value={editTable.combines_with}
                  onChange={(v) => setEditTable({ ...editTable, combines_with: v })} />
                {Number(editTable.capacity) >= 4 && (
                  <div data-testid="split-table-section" className="border-t border-border/40 pt-4">
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Split into sub-tables</Label>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Split into smaller bookable parts (e.g. two parties on one big table). A booking on the whole table blocks its parts, and vice versa.
                    </p>
                    {tables.filter((t) => t.parent_id === editTable.id).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {tables.filter((t) => t.parent_id === editTable.id).map((s) => (
                          <span key={s.id} data-testid={`subtable-${s.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border border-primary/50 bg-primary/10">
                            {s.label} ({s.capacity})
                            <button type="button" data-testid={`remove-subtable-${s.id}`} onClick={() => { removeTable(s.id); }}
                              className="text-muted-foreground hover:text-destructive">×</button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2 mt-2.5">
                      {[2, 3, 4].map((n) => (
                        <Button key={n} size="sm" variant="outline" className="rounded-full" data-testid={`split-${n}-btn`}
                          onClick={() => splitTable(editTable, n)}>
                          Split into {n}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                <Button data-testid="edit-table-save" onClick={saveEdit} disabled={!editTable.label} className="w-full rounded-full">Save table</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
